/**
 * Skill transformation layer for the Gaslight (1890s) era.
 * Applies find/replace/remove rules from the PRD to the core skill list.
 */

type SkillRow = { key: string; label: string; base_value: number | null };

const REMOVE_KEYS = new Set([
  'drive-auto',
  'electrical-repair',
  'pilot',
]);

const REPLACE_MAP: Record<string, { key: string; label: string }> = {
  // These are handled by the gaslight-skills.json directly,
  // but this utility can transform 1920s skills at runtime if needed.
  'drive-auto': { key: 'drive-carriage', label: 'Drive Carriage' },
  'pilot': { key: 'pilot-balloon', label: 'Pilot (Balloon)' },
};

export function filterSkillsForEra(skills: SkillRow[], era: '1920s' | 'gaslight' | 'modern'): SkillRow[] {
  if (era === '1920s') return skills;

  return skills
    .filter(s => !REMOVE_KEYS.has(s.key))
    .map(s => {
      const replacement = REPLACE_MAP[s.key];
      if (replacement) {
        return { ...s, ...replacement };
      }
      return s;
    });
}

export type SocialClass = 'lower' | 'middle' | 'upper';

const CLASS_CR_RANGES: Record<SocialClass, [number, number]> = {
  lower: [0, 9],
  middle: [10, 49],
  upper: [50, 99],
};

/**
 * Check if an occupation's Credit Rating range overlaps with a social class.
 */
export function occupationMatchesClass(
  creditRatingStr: string,
  socialClass: SocialClass
): boolean {
  const match = creditRatingStr.match(/(\d+)\s*-\s*(\d+)/);
  if (!match) return true; // If no CR range, allow all classes
  const [, minStr, maxStr] = match;
  const crMin = parseInt(minStr, 10);
  const crMax = parseInt(maxStr, 10);
  const [classMin, classMax] = CLASS_CR_RANGES[socialClass];
  // Overlap check
  return crMin <= classMax && crMax >= classMin;
}
